<template>
  <modal class="tag-modal" v-if="!!tags" :title="title" @close="onClose" @dismiss="$emit('dismiss')">
    <template #body>
      <div class="search-bar">
        <input
          type="text"
          class="form-control"
          placeholder="絞り込みキーワードを入力してください。"
          v-model="search.tagName"
        >
      </div>
      <div class="tags">
        <div class="system-tags">
          <div
            class="custom-control custom-checkbox"
            :class="{ 'disabled': isChoukiHozonTag(e) && isChoukiHozonTagDisabled }"
            v-for="(e, i) in systemTags"
            :key="i"
          >
            <input
              type="checkbox"
              class="custom-control-input"
              :id="'system-tag' + e.id"
              v-model="e.isSelected"
              @change="e.isPartiallySelected = false"
              :disabled="isChoukiHozonTag(e) && isChoukiHozonTagDisabled"
            >
            <label class="custom-control-label" :for="'system-tag' + e.id">
              <div class="tag-name ib ml10">
                <i class="fas fa-circle fa-xs text-warning mr4"></i>{{e.tagName}}
                <span class="font-sm" v-if="e.isPartiallySelected">({{selectedStatus(e)}})</span>
                <span class="font-sm" v-if="isChoukiHozonTag(e) && isChoukiHozonTagDisabled">
                  ※ 長期保存タグは動画再生画面で設定してください
                </span>
              </div>
            </label>
          </div>
        </div>
        <div class="user-tags">
          <div class="custom-control custom-checkbox" v-for="(e, i) in userTags" :key="i">
            <input
              type="checkbox"
              class="custom-control-input"
              :id="'user-tag' + e.id"
              v-model="e.isSelected"
              @change="e.isPartiallySelected = false"
            >
            <label class="custom-control-label" :for="'user-tag' + e.id">
              <div class="tag-name ib ml10">
                <i class="fas fa-circle fa-xs mr4"></i>{{e.tagName}}
                <span class="font-sm" v-if="e.isPartiallySelected">({{selectedStatus(e)}})</span>
              </div>
            </label>
            <div class="del-button ib ml10" v-if="e.isNew" @click="removeTag(e.id)">
              <i class="far fa-times-circle"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="tag-form" v-if="allowCreateTag">
        <input type="text" class="form-control" v-model="tagNameToAdd">
        <button class="btn btn-secondary" @click="addTag" :disabled="!tagNameToAdd">
          タグを追加
        </button>
      </div>
      <div class="errors">
        <div v-for="(msg, idx) in errorMsgs" :key="idx">{{msg}}</div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import tagApi from 'src/apis/tag';
import {
  TAG_TYPE1_SYSTEM,
  TAG_TYPE1_USER,
  TAG_NAME_CHOUKI_HOZON,
} from 'src/consts/tag';
import type { TagItem } from 'src/models/tag';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';

type NewTag = {
  id: number;
  tagName: string;
  isNew: boolean;
  isSelected: boolean;
  isPartiallySelected: boolean;
};

withDefaults(defineProps<{
  title: string;
  allowCreateTag?: boolean;
  isChoukiHozonTagDisabled?: boolean;
}>(), {
  allowCreateTag: true,
  isChoukiHozonTagDisabled: false,
});

const emit = defineEmits<{
  dismiss: [];
  close: [tags: TagItem[]];
}>();

const selectedTagsModel = defineModel<TagItem[]>({ default: () => [] });

const isTagItem = (tag: TagItem | NewTag): tag is TagItem => !!(tag as TagItem).tagType1;

const tags = reactive<Array<TagItem | NewTag>>([]);
const search = reactive({ tagName: '' });
const tagNameToAdd = ref('');
const errorMsgs = ref<string[]>([]);

const filteredTags = computed<(TagItem | NewTag)[]>(() =>
  tags.filter(e => e.tagName.includes(search.tagName)),
);
const systemTags = computed<(TagItem | NewTag)[]>(() =>
  filteredTags.value.filter(e => isTagItem(e) && e.tagType1 === TAG_TYPE1_SYSTEM),
);
const userTags = computed<(TagItem | NewTag)[]>(() =>
  filteredTags.value.filter(e => isTagItem(e) ? e.tagType1 === TAG_TYPE1_USER : e.isNew),
);

const isChoukiHozonTag = (tag: TagItem | NewTag): boolean =>
  tag.tagName === TAG_NAME_CHOUKI_HOZON;

const selectedStatus = (tag: TagItem | NewTag): string => {
  const { count, numMovies } = selectedTagsModel.value.find(e => e.id === tag.id) || {};
  return `${count}/${numMovies}の動画で選択中`;
};

const halfWidthLetterCount = (str: string): number => {
  let ret = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    const asciiCode = str.charCodeAt(i);
    ret += asciiCode < 128 ? 1 : 2;
  }
  return ret;
};

const isValidTagName = (): boolean => {
  errorMsgs.value = [];
  const tagName = tagNameToAdd.value;
  if (!tagName) errorMsgs.value.push('動画タグは必須です');
  if (halfWidthLetterCount(tagName) > 32) errorMsgs.value.push('動画タグは全角16文字以内で入力してください');
  if (tags.find(e => e.tagName === tagName)) errorMsgs.value.push('その動画タグは既に使用されています');
  return !errorMsgs.value.length;
};

const addTag = (): void => {
  if (!isValidTagName()) return;

  const nextId = tags.length ? tags[tags.length - 1].id + 1 : 0;
  const obj: NewTag = {
    id: nextId,
    tagName: tagNameToAdd.value,
    isNew: true,
    isSelected: false,
    isPartiallySelected: false,
  };
  tags.push(obj);
  tagNameToAdd.value = '';
};

const removeTag = (id: number): void => {
  const idx = tags.findIndex(e => e.id === id);
  tags.splice(idx, 1);
};

const onClose = async(): Promise<void> => {
  try {
    errorMsgs.value = [];
    const reqs: [number, TagItem][] = await Promise.all(
      tags.map((tag, i) => [tag, i] as const)
        .filter(([tag]) => tag.isNew)
        .map(async([tag, i]): Promise<[number, TagItem]> => {
          const createdTag = await tagApi.create({ tag_name: tag.tagName });
          return [i, { ...createdTag, isSelected: false, isPartiallySelected: false, isNew: false }];
        }),
    );
    for (const [i, tag] of reqs) {
      tag.isSelected = tags[i].isSelected;
      tag.isPartiallySelected = tags[i].isPartiallySelected;
      tags[i] = tag;
    }
    const selectedTags = tags.filter((e): e is TagItem => e.isSelected && isTagItem(e)).map(e => ({ ...e }));
    selectedTagsModel.value = selectedTags;
    emit('close', selectedTags);
  } catch (e) {
    errorMsgs.value = getErrorMessages(e as AxiosError);
  }
};

const getTags = async(): Promise<void> => {
  const fetchedTags = await tagApi.index();
  const selectedTagMap = selectedTagsModel.value.reduce(
    (acc: Record<number, TagItem>, e) => { acc[e.id] = e; return acc; },
    {},
  );
  const tagItems: TagItem[] = fetchedTags.map(tag => {
    const selectedTag = selectedTagMap[tag.id];
    if (!selectedTag) {
      return { ...tag, isSelected: false, isPartiallySelected: false, isNew: false };
    }

    return {
      ...tag,
      isSelected: true,
      isPartiallySelected: selectedTag.isPartiallySelected,
      isNew: false,
    };
  });
  tags.splice(0, tags.length, ...tagItems.toSorted((a, b) => a.id - b.id));
};

onMounted(getTags);
</script>

<style lang="scss" scoped>
.tags {
  margin: 32px 0px;

  .system-tags {
    margin-bottom: 32px;
  }
  .tag-name {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .del-button {
    cursor: pointer;
    color: #999999;
  }
  .disabled {
    opacity: 0.5;
    .custom-control-label {
      cursor: not-allowed;
    }
  }
  .font-sm {
    margin-left: 8px;
    font-size: 13px;
  }
}
.tag-form {
  display: grid;
  margin: 0px 20px;
  grid-template-columns: auto 90px;
  grid-column-gap: 8px;
}
.errors {
  margin-top: 16px;
  color: red;
}
</style>
